import * as React from 'react';
import classnames from 'classnames';
import { autoUpdate, useFloating } from '@floating-ui/react';
import { getAsset, ICoreContext } from '@msdyn365-commerce/core';
import { PeriodicDiscountOfferType, ProductPrice } from '@msdyn365-commerce/retail-proxy';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Button } from '@msdyn365-commerce-modules/utilities';
import { IProductListResources, ProductListProduct } from '../../product-list-component/product-list-component';
import { IProductListCompactComponentProps } from '../product-list-compact-component';
import QuantityBreakoffSelector from '../../common/quantity-breakoff-selector';
import QuantityInput from '../../common/quantity-input';
import { IProductAgreement, IProductError, SimpleProductExt } from '../../../shared-utils/types';
import { dividePriceBy, multiplyPriceBy } from '../../../shared-utils/price-utils';
import PriceComponent from '../../../themes/dlvry-adventureworks/views/components/price.component';
import { getLocale } from '../../product-list-component/locale';
import AddToOrderTemplateComponent from '../../../themes/dlvry-adventureworks/views/components/addtoordertemplate.component';
import { generateAddToOrderTemplateDialogResources, generateDuplicateItemsWhenAddingToOrderTemplateDialogResources, generateItemAddedToOrderTemplateDialogResources, generateOrderTemplateNameDialogResources } from '../../product-list-component/locale/add-to-order-template-resources';
import WishlistButton from './wishlist-button';
import { toast } from '../../toast/toast';
import PQueue from 'p-queue';
import ProductImage from './product-image';
import { getNonBreakoffUOM, getTotalConversionText } from '../../../shared-utils/unit-conversion-utils';
import { CartHelper } from '../../../shared-utils/cart-helper';
import CartCount from '../../cart-count';
import Availability from './availability';
import { LifecycleStatus } from '../../../shared-utils/lifecycle-status';
import { isAlternativeProductsEnabledForOUN } from '../../../settings/app.settings.utils';
import { IProductRelationTypeForProduct, SupportedRelationTypes } from '../../../actions/get-relation-types-by-ids';
import { LocalDropdown } from '../../dropdown/dropdown';
import { AlternativeProducts } from '../../dropdown/alternative-products';
import * as GTM from '../../../shared-utils/google-tag-manager/gtm';

export interface IProductLineProps extends IProductListCompactComponentProps {
    baseClassName: string;
    locale: ReturnType<typeof getLocale>;
    product: ProductListProduct;
    disableBuyButton?: boolean;
    alternativeProductsMode?: 'edit' | 'view';
    isDragOver?: 'over' | 'under' | undefined;
    isSelected?: boolean;
    showMore: boolean;
    cartQueue: PQueue;
    error?: IProductError;
    customerAgreements?: IProductAgreement[];
    setShowMore: (value: boolean) => void;
    onBreakoffChange: (product: ProductListProduct, breakoff: boolean) => void;
    onDragOver?: (recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onDropLine?: (event: React.DragEvent<HTMLTableRowElement>, recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onIsSelectedChange?: (value: boolean) => void;
    hideAvailability?: boolean;
}

interface IPriceProps {
    className: string;
    context: ICoreContext;
    id: string;
    typeName: string;
    quantity: number;
    price: number | undefined;
    product: ProductListProduct;
    simpleProduct: SimpleProductExt | undefined;
    resources: IProductListResources;
    initialBreakoffEnabled: boolean | undefined;
    showHistoricalPrice?: boolean;
}

const Price = (props: IPriceProps) => {
    const {
        className,
        context,
        id,
        typeName,
        quantity,
        price,
        product,
        simpleProduct,
        resources,
        initialBreakoffEnabled,
        showHistoricalPrice
    } = props;

    if (!context.request.user.isAuthenticated) {
        return null;
    }

    if (product.PriceFailed) {
        return <span className={`${className}__error`}>{resources.priceErrorText || 'Pris n/a'}</span>;
    }

    if (!price || !simpleProduct) {
        return <span className={`${className}__retrieving`}>-</span>;
    }

    let productPrice: ProductPrice | undefined;

    // If not use latest price if available otherwise whatever is set on the product
    productPrice = product.ProductPrice ? product.ProductPrice : {
        CustomerContextualPrice: price,
        BasePrice: product.BasePrice
    };

    if (!showHistoricalPrice && !initialBreakoffEnabled && simpleProduct.Behavior?.MultipleOfQuantity) {
        productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
    }

    if (showHistoricalPrice && productPrice.CustomerContextualPrice) {
        if (initialBreakoffEnabled) {
            productPrice = dividePriceBy(productPrice, quantity || 1);
        } else {
            productPrice = dividePriceBy(productPrice, quantity || 1);
            productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior?.MultipleOfQuantity || 1);
        }
    }

    return (
        <PriceComponent
            context={context}
            data={{ price: productPrice }}
            id={id}
            typeName={typeName}
            className={`${className}`}
            discountPercentageSide='right'
            hideBeforePrice />
    );
};

const ProductLine = (props: IProductLineProps) => {
    const {
        mode = 'default',
        moduleId,
        moduleType,
        resources,
        context,
        baseClassName,
        locale,
        allowMultiSelect,
        allowReorder,
        disableQuantityInput,
        disableQuantityInputForProducts,
        isSelected,
        product,
        simpleProducts,
        customerInformation,
        showMore,
        showPrices,
        cartQueue,
        validatedProductAccess,
        setShowMore,
        onBreakoffChange,
        onUpdateQuantity,
        onBuyValidate,
        retrieveOrderTemplates,
        onUpdateOrderTemplates,
        onIsSelectedChange
    } = props;
    const { request } = context;
    const { user } = request;

    const [loading, setLoading] = React.useState(false);
    const [quantity, setQuantity] = React.useState<number>(product.CurrentQuantity || 0);

    React.useEffect(() => {
        setQuantity(product.CurrentQuantity || 0);
    }, [product.CurrentQuantity]);

    const { refs, floatingStyles } = useFloating({
        placement: 'bottom-end' as any,
        whileElementsMounted: autoUpdate,
        open: showMore,
        onOpenChange: setShowMore
    });

    const productUrl = product ? getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined) : '';
    const price = showPrices !== false
        ? (product.Price && product.Price !== 0.00)
            ? product.Price : undefined
        : undefined;
    const simpleProduct = simpleProducts?.find((sp) => sp.RecordId === product.RecordId);
    const lifeCycleStatus = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    const isExpired = simpleProduct?.Behavior?.IsSaleAtPhysicalStoresAllowed === false ||
        simpleProduct?.Behavior?.IsSaleAtSelfCheckoutRegistersAllowed === false ||
        lifeCycleStatus === LifecycleStatus.OutOfSeason;
    const relationTypesForProduct = props.relationTypesByIds?.filter((relationType: IProductRelationTypeForProduct) => relationType.ProductId === product.RecordId) || [];
    const showAlternativeProducts = isAlternativeProductsEnabledForOUN(context) && relationTypesForProduct.length > 0;
    const showAlternativeProductsType: 'Primary' | 'Secondary' | undefined =
        showAlternativeProducts && relationTypesForProduct.some(x => x.Name === SupportedRelationTypes.ReplacementHard) && product.Availability?.StockLevelCode === 'OOS'
            ? 'Primary'
            : showAlternativeProducts
                ? 'Secondary'
                : undefined;

    const addProductToCart = async (product: ProductListProduct, simpleProduct: SimpleProductExt | undefined) => {
        if (!context || !product || !simpleProduct || !context.request.user.catalogId) {
            return;
        }

        const breakoff = props.breakoffProducts?.[product.BreakoffProductKey] || false;
        const success = await CartHelper.addToCartAsync(
            context,
            product,
            simpleProduct,
            validatedProductAccess?.find(x => x.RecordId === product.RecordId)?.HasAccess,
            quantity,
            breakoff,
            onBuyValidate ? async () => onBuyValidate(product, quantity) : undefined,
            getNonBreakoffUOM(simpleProduct, props.unitConversions)
        );

        if (success) {
            setQuantity(0);
            props.onUpdateQuantity(product, 0, false);
        }
    };
    const onBuyClick = async (
        product: ProductListProduct,
        simpleProduct: SimpleProductExt | undefined,
        setLoading: (value: boolean) => void
    ) => {
        setLoading(true);
        await cartQueue.add(() => addProductToCart(product, simpleProduct));
        setLoading(false);
    };
    const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();

        const offsetX = e.clientX - (e.target as Element).getBoundingClientRect().left;
        const offsetY = e.clientY - (e.target as Element).getBoundingClientRect().top;

        e.dataTransfer.setData('product', JSON.stringify({
            ProductName: product.Name || '',
            ProductRecordId: product.RecordId || 0,
            ProductUnitOfMeasure: product.DefaultUnitOfMeasure || ''
        }));
        e.dataTransfer.setDragImage((e.target as Element).parentElement as Element, offsetX, offsetY);
        e.dataTransfer.effectAllowed = 'copyMove';
    };
    const onQuantityChange = (newQuantity: number, applyImmediately: boolean) => {
        setQuantity(newQuantity);
        onUpdateQuantity(product, newQuantity, applyImmediately);
    };

    const breakoff = props.breakoffProducts?.[product.BreakoffProductKey] || false;
    const totalConversionText = getTotalConversionText(
        context,
        simpleProduct,
        props.unitConversions,
        quantity,
        breakoff,
        product
    );

    const hasMixAndMatchDiscount = product.ProductPrice?.DiscountLines
        ?.some(discount => discount.PeriodicDiscountTypeValue === PeriodicDiscountOfferType.MixAndMatch);
    const netPrice = product.ProductPrice && 'NetPrice' in product.ProductPrice ? (product.ProductPrice as any).NetPrice as number : undefined;
    const totalAmount = product.ProductPrice && 'TotalAmount' in product.ProductPrice ? (product.ProductPrice as any).TotalAmount as number : undefined;
    const totalPrice: ProductPrice = {
        CustomerContextualPrice: props.showHistoricalPrice
            ? price
            : hasMixAndMatchDiscount
                ? totalAmount || 0
                : (price || 0) * quantity,
        BasePrice: hasMixAndMatchDiscount ? netPrice : undefined,
        DiscountLines: hasMixAndMatchDiscount && netPrice ? product.ProductPrice?.DiscountLines : undefined
    };

    return (
        <tr
            key={`${product.RecordId}-${product.DefaultUnitOfMeasure}`}
            data-product-id={product.RecordId}
            className={classnames({
                'drag-over': props.isDragOver,
                [props.isDragOver || '']: props.isDragOver
            })}
            onDrop={(e) => {
                e.stopPropagation(); // Prevent onDrop handler on parent from firing
                props.onDropLine?.(e, product.RecordId, props.isDragOver);
            }}
            onDragOver={(e) => {
                e.stopPropagation();

                if (props.allowReorder && e.dataTransfer.types.includes('product')) {
                    e.preventDefault();

                    const rect = (e.currentTarget as Element).getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const halfHeight = rect.height / 2;
                    const overOrUnder: 'over' | 'under' = y > halfHeight ? 'under' : 'over';

                    props.onDragOver?.(product.RecordId, overOrUnder);
                }
            }}
        >
            {(allowMultiSelect || allowReorder) && (
                <td className='col-drag-select no-side-padding'>
                    <div>
                        {allowReorder && (
                            <div
                                className='drag-area'
                                draggable={props.allowReorder}
                                onDragStart={props.allowReorder ? onDragStart : undefined}
                                onDragEnd={() => props.onDragOver?.(undefined, undefined)}
                                onDragOver={allowReorder ? (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                } : undefined}

                            >
                                <img className='drag-area__icon' src={getAsset('icons/drag-handle.svg', context.request)} />
                            </div>
                        )}

                        {allowMultiSelect && (
                            <label className='checkbox-container'>
                                <input
                                    aria-label='selectOneRowText'
                                    className='checkbox-input'
                                    type='checkbox'
                                    checked={isSelected}
                                    aria-checked={isSelected}
                                    onChange={(e) => onIsSelectedChange?.(e.target.checked)}
                                />
                                <span className='checkmark'/>
                            </label>
                        )}
                    </div>
                </td>
            )}
            <td className='no-padding'>
                <div style={{ position: 'relative' }}>
                    <a href={productUrl} title={product.Name}>
                        <ProductImage
                            {...props}
                            className='product-image'
                            heading={product.Name || ''}
                            product={product}
                            imageSettings={props.imageSettings}
                        />
                    </a>
                </div>
            </td>
            {/* <td className='bold uppercase grayed'>{product.Producer}</td> */}
            <td className='bold'>
                <div className={`${baseClassName}__product__producer`}>{product.Producer}</div>
                <a className={`${baseClassName}__product__link`} href={productUrl}>
                    {props.error && (
                        <span>
                            <i
                                className={`${baseClassName}__product__${props.error.Type}`}
                                title={props.error.Error} />
                            <span className='sr-only'>{props.error.Error}</span>
                        </span>
                    )}
                    {props.customerAgreements?.some(x => product.Agreements?.find(y => y.AttributeRecordId === x.AttributeRecordId)) && (
                        <span className={`${baseClassName}__product__agreement-product`} title={resources.agreementProductTooltip || 'Avtalevare'}>A</span>
                    )}
                    <span>{product.Name}</span>
                </a>
            </td>
            {user.isAuthenticated && !props.hideAvailability && (
                <td style={{ whiteSpace: 'nowrap', width: '1%' }}>
                    <Availability baseClassName={baseClassName} product={product} simpleProduct={simpleProduct} />
                </td>
            )}
            <td>{product.ItemId}</td>
            <td className='price-summary nowrap no-side-padding'>
                {!user.isAuthenticated && (
                    <div className={`${baseClassName}__price__not-logged-in`}>
                        {resources.priceNotLoggedInText || 'Logg inn for å se pris'}
                    </div>
                )}
                {user.isAuthenticated && lifeCycleStatus === LifecycleStatus.OutOfSeason && (
                    <div className={`${baseClassName}__price__out-of-season`}>
                        {(resources as any).priceOutOfSeasonText || 'Ute av sesong'}
                    </div>
                )}
                {user.isAuthenticated && lifeCycleStatus !== LifecycleStatus.OutOfSeason && (
                    <Price
                        className={`${baseClassName}__price`}
                        context={context}
                        id={moduleId}
                        typeName={moduleType}
                        resources={resources}
                        quantity={quantity}
                        price={price}
                        product={product}
                        simpleProduct={simpleProduct}
                        initialBreakoffEnabled={breakoff}
                        showHistoricalPrice={props.showHistoricalPrice}
                    />
                )}
            </td>
            <td className='quantity no-side-padding right-align'>
                {user.isAuthenticated && !props.readOnly && (
                    <div>
                        <QuantityBreakoffSelector
                            context={context}
                            disable={isExpired || disableQuantityInput || disableQuantityInputForProducts?.includes(product.RecordId)}
                            product={simpleProduct}
                            breakoff={breakoff}
                            quantity={quantity}
                            unitConversions={props.unitConversions}
                            onBreakoffChange={(breakoff) => onBreakoffChange(product, breakoff)}
                            onQuantityChange={(quantity) => onQuantityChange(quantity, true)}
                        />
                        <QuantityInput
                            context={context}
                            disable={isExpired || disableQuantityInput || disableQuantityInputForProducts?.includes(product.RecordId)}
                            product={simpleProduct}
                            breakoff={breakoff}
                            quantity={quantity}
                            onQuantityChange={onQuantityChange}
                            nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
                            allowZero={props.allowZeroQuantity}
                        />
                        <div className={`${baseClassName}__total-conversion-text`}>
                            {totalConversionText && (
                                <>
                                    <span>
                                        =
                                    </span>
                                    <span>
                                        {totalConversionText}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </td>
            {user.isAuthenticated && props.showTotalPrices && (
                <td className='price-summary total-price'>
                    {lifeCycleStatus !== LifecycleStatus.OutOfSeason && (
                        <div className={classnames(`${baseClassName}__total-price`, { retrieving: price === undefined })} data-failed={props.product.PriceFailed}>
                            <b>
                                <PriceComponent
                                    context={props.context}
                                    data={{ price: totalPrice }}
                                    id={props.moduleId}
                                    typeName={props.moduleType}
                                    freePriceText={price === undefined ? '-' : undefined}
                                    hideBeforePrice />
                                {hasMixAndMatchDiscount && (
                                    <div>
                                        <span className={`${baseClassName}__total-price__mix-and-match-discount`}>
                                            {/* locale.cartButton.mixAndMatchDiscountText || */ 'Mix & Match'}
                                        </span>
                                    </div>
                                )}
                            </b>
                        </div>
                    )}
                </td>
            )}
            {mode !== 'cart' && (
                <td className='no-side-padding right-align nowrap'>
                    {user.isAuthenticated && (
                        <CartCount
                            context={context}
                            cart={props.cart?.cart}
                            product={simpleProduct}
                            nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
                            inCartTooltip='{0} {1} i handlekurven'
                        />
                    )}

                    {user.isAuthenticated && (!props.readOnly && !isExpired) && (
                        <>
                            {showAlternativeProducts && (
                                <LocalDropdown
                                    trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
                                        <button
                                            ref={ref as any}
                                            className={classnames(`${baseClassName}__change-product__button`, { primary: showAlternativeProductsType === 'Primary' })}
                                            disabled={props.disableBuyButton}
                                            onClick={() => {
                                                void GTM.beginAltProductReplaceedAsync(product);
                                                onClick();
                                            }}
                                            aria-expanded={ariaExpanded}
                                            title={resources.alternativeProductsButtonText || 'Se alternativer'}
                                        />
                                    )}
                                >
                                    {(close) => (
                                        <AlternativeProducts
                                            id={moduleId}
                                            typeName={moduleType}
                                            context={context}
                                            mode={props.alternativeProductsMode || 'view'}
                                            resources={{
                                                alternativeProductsChangeProductButtonText: resources.alternativeProductsChangeProductButtonText,
                                                alternativeProductsLoadingText: resources.alternativeProductsLoadingText,
                                                alternativeProductsNoResultsText: resources.alternativeProductsNoResultsText,
                                                alternativeProductsErrorText: resources.alternativeProductsErrorText
                                            }}
                                            product={product}
                                            relationTypesByIds={props.relationTypesByIds}
                                            onChangeProduct={props.onChangeProduct}
                                            close={close}
                                        />
                                    )}
                                </LocalDropdown>
                            )}

                            {showAlternativeProductsType !== 'Primary' && (
                                <Button
                                    className={classnames(`${baseClassName}__buy-button`, { loading })}
                                    disabled={props.disableBuyButton}
                                    onClick={() => onBuyClick(product, simpleProduct, setLoading)}
                                >
                                    {resources.buyNowButtonText}
                                </Button>
                            )}
                        </>
                    )}

                    {user.isAuthenticated && isExpired && (
                        <Button className={classnames(`${baseClassName}__buy-button`)} disabled={true}>
                            {lifeCycleStatus === LifecycleStatus.OutOfSeason ? locale.cartButton.outOfSeason : locale.cartButton.expired}
                        </Button>
                    )}
                </td>
            )}
            <td className='no-side-padding'>
                <div className={`${baseClassName}__right-actions`}>
                    {user.isAuthenticated && (
                        <>
                            <WishlistButton
                                context={context}
                                resources={resources}
                                wishlists={props.wishlists}
                                defaultWishlistName={props.defaultWishlistName}
                                locale={locale}
                                product={product}
                                onClick={() => setShowMore(false)}
                            />

                            {(retrieveOrderTemplates && (
                                <AddToOrderTemplateComponent
                                    className='order-template-button'
                                    addToOrderTemplateButtonText={locale.addToOrderTemplate.addToOrderTemplateButtonText || 'addToOrderTemplateButtonText'}
                                    addToOrderTemplateButtonTooltip={locale.addToOrderTemplate.addToOrderTemplateButtonTooltip || 'addToOrderTemplateButtonTooltip'}
                                    addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(locale)}
                                    createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(locale)}
                                    itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(locale)}
                                    duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(locale)}
                                    context={context}
                                    id={moduleId}
                                    typeName={moduleType}
                                    disabled={!simpleProduct || !customerInformation}
                                    disableButton={!simpleProduct || !customerInformation}
                                    data={{
                                        partOfProductLists: product.PartOfProductLists,
                                        product: simpleProduct || {} as any,
                                        quantity: quantity || 1,
                                        customerInformation: customerInformation || { IsB2b: true } as any // Always pass 'IsB2b' so that it will always render (unless false from api)
                                    }}
                                    showButtonText
                                    onClick={() => setShowMore(false)}
                                    onSuccess={async (orderTemplate) => {
                                        await onUpdateOrderTemplates?.(product, orderTemplate);
                                        toast({
                                            text: `${product.Name}${locale.addToOrderTemplate.itemAddedToOrderTemplateHeaderMessageText}`,
                                            autoClose: 3000,
                                            canClose: true,
                                            position: 'bottom-right',
                                            type: 'success',
                                            showProgress: true
                                        });
                                    }}
                                    retrieveOrderTemplates={retrieveOrderTemplates}
                                />
                            ))}

                            {props.onProductRemove && (
                                <button
                                    className={classnames(`${baseClassName}__btn-remove`)}
                                    title={props.resources.removeButtonText || 'Fjern'}
                                    onClick={async () => {
                                        setShowMore(false);
                                        await props.onProductRemove?.(product);
                                    }}
                                >
                                    <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.7404 9L14.3942 18M9.60577 18L9.25962 9M19.2276 5.79057C19.5696 5.84221 19.9104 5.89747 20.25 5.95629M19.2276 5.79057L18.1598 19.6726C18.0696 20.8448 17.0921 21.75 15.9164 21.75H8.08357C6.90786 21.75 5.93037 20.8448 5.8402 19.6726L4.77235 5.79057M19.2276 5.79057C18.0812 5.61744 16.9215 5.48485 15.75 5.39432M3.75 5.95629C4.08957 5.89747 4.43037 5.84221 4.77235 5.79057M4.77235 5.79057C5.91878 5.61744 7.07849 5.48485 8.25 5.39432M15.75 5.39432V4.47819C15.75 3.29882 14.8393 2.31423 13.6606 2.27652C13.1092 2.25889 12.5556 2.25 12 2.25C11.4444 2.25 10.8908 2.25889 10.3394 2.27652C9.16065 2.31423 8.25 3.29882 8.25 4.47819V5.39432M15.75 5.39432C14.5126 5.2987 13.262 5.25 12 5.25C10.738 5.25 9.48744 5.2987 8.25 5.39432" stroke="#0F172A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                    </svg>
                                </button>
                            )}

                            {/* <button
                                ref={refs.setReference as any}
                                className={classnames(`${baseClassName}__more-button`, { open: showMore })}
                                onClick={() => setShowMore(!showMore)}
                                title='Mer'
                            >
                                <svg width="12.98px" height="12.98px" version="1.1" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
                                    <path d="m603.12 51.359c66 0 120 54 120 120s-54 120-120 120-120-54-120-120 54-120 120-120zm-120 960c0 66 54 120 120 120s120-54 120-120-54-120-120-120-120 54-120 120zm0-420c0 66 54 120 120 120s120-54 120-120-54-120-120-120-120 54-120 120z"/>
                                </svg>
                            </button> */}
                        </>
                    )}
                </div>

                {(
                    <div
                        ref={refs.setFloating}
                        className={classnames(`${baseClassName}__more-popup`, { open: showMore })}
                        style={floatingStyles}
                    >
                    </div>
                )}
            </td>
        </tr>
    );
};

export default ProductLine;
